/**
 * Ember Data Collection Types
 *
 * These types extend standard arrays to include the metadata that Ember Data
 * adds to collection responses from store.query() and store.findAll().
 *
 * Usage:
 *   import type { PaginatedCollection } from 'tio-common/types/ember-data';
 *
 *   interface Args {
 *     model: PaginatedCollection<EmployeeModel>;
 *   }
 */

/**
 * Pagination metadata from JSON:API responses
 */
export interface PaginationMeta {
  page?: {
    total?: number;
    limit?: number;
    offset?: number;
  };
  /** Full distinct sorted list of facet values for the whole collection, independent of active filters. */
  keys?: string[];
  /** Full distinct sorted list of facet values for the whole collection, independent of active filters. */
  levels?: string[];
}

/**
 * Extended array type for Ember Data collections that includes:
 * - meta: JSON:API pagination metadata
 * - loading: Whether the collection is being loaded
 * - isLoading: Alias for loading state
 * - isLoaded: Whether the collection has finished loading
 *
 * @template T - The model type in the collection
 */
export type PaginatedCollection<T> = T[] & {
  meta?: PaginationMeta | null;
  loading?: boolean;
  isLoading?: boolean;
  isLoaded?: boolean;
};

/**
 * Adapt a WarpDrive collection response into a `PaginatedCollection`.
 *
 * `store.request(query(...))` resolves to `{ content }`, where `content.data`
 * is the records array and `content.meta` carries the JSON:API pagination meta
 * (`meta.page.total`). Returning bare `content.data` drops that meta, which
 * silently collapses `<Paginator>` to a single page (total → 0 → lastPage → 1),
 * stranding users on page one. This copies the records into a
 * `PaginatedCollection` and re-attaches meta so templates can keep reading
 * `model.meta.page.total`.
 */
export function toPaginatedCollection<T>(content: {
  data: T[];
  meta?: PaginationMeta | null;
}): PaginatedCollection<T> {
  const collection = [...content.data] as PaginatedCollection<T>;
  collection.meta = content.meta ?? null;
  // Callers await store.request() before adapting, so the collection is fully
  // resolved. Expose the load flags the legacy store.query() arrays carried so
  // templates reading `@model.isLoading` / `{{not @model.isLoaded}}` see the
  // resolved state instead of undefined.
  collection.loading = false;
  collection.isLoading = false;
  collection.isLoaded = true;
  return collection;
}
